import { useState, useEffect, useMemo, useRef } from 'react';
import { FiChevronDown, FiSearch } from 'react-icons/fi';
import styles from './LedgerAccountSelect.module.css';

/**
 * Generic single-select dropdown with a search box.
 * Shares the LedgerAccountSelect styling for a consistent look.
 *
 * Props:
 *   value        — selected option value (string | number)
 *   onChange     — (value: string) => void
 *   options      — array of { value, label } (or pass `getLabel`/`getValue` for raw objects)
 *   placeholder  — trigger label when nothing is selected
 *   disabled     — disables the trigger
 *   searchPlaceholder — placeholder for the search input
 *   allowCustom  — when true, allows selecting a free-text value not present in `options`
 *                  via a "Use '...'" row, and shows that typed value as the trigger label
 */
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = '— Select —',
  disabled = false,
  searchPlaceholder = 'Search…',
  allowCustom = false,
}) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref       = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, []);

  useEffect(() => {
    if (open) { setTimeout(() => searchRef.current?.focus(), 0); }
    else       { setSearch(''); }
  }, [open]);

  const hits = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [options, search]);

  const selected = useMemo(
    () => (value !== '' && value != null ? options.find((o) => String(o.value) === String(value)) || null : null),
    [value, options],
  );

  const triggerLabel = selected ? selected.label : (allowCustom && value ? String(value) : null);

  return (
    <div ref={ref} className={styles.select}>
      <button
        type="button"
        className={styles.trigger}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span className={`${styles.triggerText} ${!triggerLabel ? styles.triggerPlaceholder : ''}`}>
          {triggerLabel || placeholder}
        </span>
        <FiChevronDown className={styles.triggerIcon} size={15} />
      </button>

      {open && (
        <div className={styles.panel}>
          <div className={styles.searchRow}>
            <FiSearch size={13} className={styles.searchIcon} />
            <input
              ref={searchRef}
              className={styles.searchInput}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {allowCustom && search.trim() && !hits.some((o) => String(o.label).toLowerCase() === search.trim().toLowerCase()) && (
            <div
              className={styles.option}
              onClick={() => { onChange(search.trim()); setOpen(false); }}
            >
              <span className={styles.optionName}>Use "{search.trim()}"</span>
            </div>
          )}

          {hits.length === 0 && !allowCustom && <div className={styles.empty}>No matches found</div>}

          {hits.map((o) => {
            const isSel = String(o.value) === String(value);
            return (
              <div
                key={o.value}
                className={`${styles.option} ${isSel ? styles.optionSelected : ''}`}
                onClick={() => { onChange(String(o.value)); setOpen(false); }}
              >
                <span className={styles.optionName}>{o.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
